<script setup>

import { inject, ref, computed, watch } from 'vue';
import { 
	getClasseValeur } from '@/scripts/global.js';
import { apiModifierValeurs } from '@/services/ServiceModele.js';
import TexteSaisieValeur from '@/components/generiques/TexteSaisieValeur.vue';

const { valeur } = defineProps({
  valeur: Object
});

const getTypeObjet = inject('getTypeObjet');
const getChamp = inject('getChamp');
const getDimension = inject('getDimension');
const getImportFichier = inject('getImportFichier');
const donnerFocusAuRapport = inject('donnerFocusAuRapport');
const appelerApiModifModele = inject('appelerApiModifModele');

const texte = ref(valeur.valFormat);
const modeEdition = ref(false);

const typeDonnee = computed(() => {
	const coord = valeur?.coord;
	if (coord?.idChamp) {
		return getChamp(coord.idTypeObjet, coord.idChamp).typeDonnee;
	} else if (coord?.idDimension) {
		return getDimension(coord.idDimension).typeDonnee;
	}
});

function activerModeEdition() {
	texte.value = valeur.valBrute;
	modeEdition.value = true;
}

function desactiverModeEdition() {
	texte.value = valeur.valFormat;
	modeEdition.value = false;
	donnerFocusAuRapport();
}

function getInfoBulle() {
	let texteDescValeur = '';
	let texteDescImport = '';
	const coord = valeur.coord;
	if (coord) {
		texteDescValeur = '<b>Valeur élémentaire</b>\n';
		if (coord.idChamp) {
			const nomTypeObjet = getTypeObjet(coord.idTypeObjet).nom;
			const nomObjet = coord.idObjet;
			const nomChamp = getChamp(coord.idTypeObjet, coord.idChamp).nom;
			texteDescValeur += `${nomTypeObjet} :\n   ${nomObjet}\nChamp :\n   ${nomChamp}\n`;
		} else if (coord.idDimension) {
			const nomDimension = getDimension(coord.idDimension).nom;
			texteDescValeur += `Dimension :\n   ${nomDimension}\n`;
		} else if (coord.idTypeObjetPopule) {
			const nomTypeObjet = getTypeObjet(coord.idTypeObjetPopule).nom;
			texteDescValeur += `Population du type d'objet :\n   ${nomTypeObjet}\n`;
		}
		if (coord.coordDim) {
			let textDescCoord = '';
			for (const c of coord.coordDim) {
				textDescCoord += `${getDimension(c.idDimension).nom} :\n   ${c.coord}\n`;
			}
			texteDescValeur += `${textDescCoord}`;
		}
		if (valeur.idImport) {
			const nomFichier = getImportFichier(valeur.idImport).nomFichier;
			texteDescImport = `\n\nPar import du fichier :\n   ${nomFichier}`;
		}
	}
	let texteErreur = '';
	const erreur = valeur.erreur;
	if (erreur) {
		texteErreur = `\n\n${getDescErreur(erreur)}`;
	}
	let texteDerniereModif = '';
	if (valeur.derniereModif) {
		texteDerniereModif = `\nDernière modification :\n   ${(new Date(valeur.derniereModif)).toLocaleString()} (${valeur.auteur})`;
	}
	const texte = `${texteDescValeur}${texteDerniereModif}${texteDescImport}${texteErreur}`;
	return {
		value: texte, 
		escape: true
	};
}

function getDescErreur(erreur) {
	switch(erreur.type) {
		case 'VALEUR_MANQUANTE':
			return `Valeur manquante pour le calcul de :\n   ${erreur.desc[0]}`;
		case 'ENTREE_EN_ERREUR':
			return `Valeur d'entrée en erreur :\n   ${erreur.desc[0]}`;
		case 'FORMULE_EN_ERREUR':
			return 'Formule en erreur';
		case 'INDICE_LISTE_INVALIDE':
			return `Indice de liste invalide : ${erreur.desc[0]} (liste de taille ${erreur.desc[1]})`;
		case 'UNITE_TEMPS_INVALIDE':
			return `Unité de temps invalide : ${erreur.desc[0]} (doit être DAYS, MONTHS, YEARS...)`;
		case 'EXPRESSION_EN_ERREUR':
			return 'Expression en erreur';
		case 'REFERENCE_CIRCULAIRE':
			return `Référence circulaire :\n   ${erreur.desc.join('\n=> ')}`;
		default:
			return '';
	}
}

function modifierValeur(coordValeur, texte) {
	if (coordValeur) {
		const modifValeur = { 
			valeur: texte, 
			coord: coordValeur
		};
		appelerApiModifModele(apiModifierValeurs, [modifValeur]);
	}
}

watch(() => valeur, (val) => {
	texte.value = val.valFormat;
	modeEdition.value = false;
})

</script>

<template>
	<div>
		<TexteSaisieValeur
			class="valeur"
            :class="modeEdition ? null : getClasseValeur(valeur)"
			:style="{color: !valeur.coord ? 'grey' : null}"
            v-model:texte="texte" 
			v-tooltip.left="getInfoBulle()"
			:typeDonnee="typeDonnee"
			@focus="activerModeEdition()"
			@validation="modifierValeur(valeur.coord, texte)"
			@annulation="desactiverModeEdition()" />
	</div>
</template>

<style scoped>

.valeur {
	width: 100%;
	border: solid 1px var(--couleur-bordure);
	border-radius: 2px;
	min-height: 1rem;
	padding: 2px 5px;
}

</style>